import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Calendar,
  Phone,
  Contact,
  Building2,
  BarChart3,
  Settings,
} from 'lucide-react';
import { TabsTrigger } from '@/components/ui/tabs';
import { AnimatedTabsList } from '@/components/ui/animated-tabs-list';
import { cn } from '@/lib/utils';
import { AGENT_TABS, type AgentTabValue } from '@/utils/agentTabs';

const TAB_ICONS: Record<AgentTabValue, React.ElementType> = {
  calendar: Calendar,
  conversations: Phone,
  contacts: Contact,
  business: Building2,
  usage: BarChart3,
  settings: Settings,
};

export type { AgentTabValue };

const agentTabListClassName =
  'w-full justify-center overflow-x-auto rounded-full bg-transparent p-1';

const agentTabTriggerClassName =
  'relative z-10 flex items-center gap-2 rounded-full bg-transparent data-[state=active]:bg-transparent';

const mobileBottomTabIndicatorClassName =
  'bottom-1 top-auto h-0.5 rounded-full bg-[hsl(var(--brand))] shadow-none';

const mobileTabTriggerClassName =
  'relative z-10 flex h-full min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-none bg-transparent px-1 py-2 text-[10px] font-medium leading-tight text-muted-foreground shadow-none data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none';

interface AgentTabNavigationProps {
  activeTab: AgentTabValue;
  placement: 'top' | 'bottom';
  showCalendarTab?: boolean;
}

export const AgentTabNavigation: React.FC<AgentTabNavigationProps> = ({
  activeTab,
  placement,
  showCalendarTab = true,
}) => {
  const { t } = useTranslation();
  const isBottom = placement === 'bottom';

  const tabs = AGENT_TABS.filter(
    tab => tab.value !== 'calendar' || showCalendarTab
  ).map(tab => {
    const Icon = TAB_ICONS[tab.value];
    return (
      <TabsTrigger
        key={tab.value}
        value={tab.value}
        className={
          isBottom ? mobileTabTriggerClassName : agentTabTriggerClassName
        }
      >
        <Icon className={cn(isBottom ? 'h-5 w-5' : 'h-4 w-4')} />
        <span className={isBottom ? 'truncate' : 'hidden sm:inline'}>
          {t(tab.labelKey, tab.fallbackLabel)}
        </span>
      </TabsTrigger>
    );
  });

  if (isBottom) {
    return (
      <nav
        className="glass-surface fixed bottom-0 left-0 right-0 z-50 overflow-hidden rounded-t-xl border-x-0 border-b-0 text-card-foreground pb-[env(safe-area-inset-bottom,0px)] lg:hidden"
        aria-label={t(
          'agent.page.tabs.navigation.translation',
          'Agent sections'
        )}
      >
        <AnimatedTabsList
          activeValue={activeTab}
          className="h-16 w-full justify-around rounded-none bg-transparent p-0 shadow-none"
          activeBrandShadow={false}
          indicatorClassName={mobileBottomTabIndicatorClassName}
        >
          {tabs}
        </AnimatedTabsList>
      </nav>
    );
  }

  return (
    <AnimatedTabsList
      activeValue={activeTab}
      className={cn(agentTabListClassName, 'hidden lg:flex')}
      activeBrandShadow
    >
      {tabs}
    </AnimatedTabsList>
  );
};

export const agentTabContentClassName =
  'mt-4 data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:slide-in-from-bottom-1 data-[state=active]:duration-200 max-lg:pb-20';
